import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gem, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError, uploadImages } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VISIBILITIES } from "@/lib/mtg";
import { EmptyState, PageHeader, Pager } from "@/components/common/PageParts";
import { VisibilityBadge } from "@/components/common/Badges";
import { ImageDropzone } from "@/components/common/ImageDropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function CardBackTile({ back, onDelete }) {
  return (
    <div className="group rise" data-testid={`card-back-tile-${back.card_back_id}`}>
      <div className="card-frame shimmer aspect-[63/88]">
        <img src={assetUrl(back.image_url)} alt={back.name} loading="lazy" className="h-full w-full object-cover" />
        {back.visibility !== "public" && <div className="absolute right-2 top-2"><VisibilityBadge visibility={back.visibility} /></div>}
        {onDelete && (
          <button onClick={() => onDelete(back)} className="absolute bottom-2 right-2 rounded-md bg-black/70 p-2 text-red-300 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
            aria-label={`Delete ${back.name}`} data-testid={`card-back-delete-${back.card_back_id}`}><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
      <p className="mt-2.5 line-clamp-1 text-sm font-semibold text-slate-100">{back.name}</p>
      {back.owner && <Link to={`/u/${back.owner.username}`} className="text-xs text-slate-500 hover:text-amber-200">by @{back.owner.username}</Link>}
    </div>
  );
}

function UploadBackDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", description: "", visibility: "public" });
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async ([file]) => {
    setBusy(true);
    try {
      const [res] = await uploadImages([file], "card_back");
      if (!res.ok) throw new Error(res.error);
      setImage(res);
      if (!form.name) setForm((f) => ({ ...f, name: file.name.replace(/\.[^.]+$/, "") }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!image) return toast.error("Choose an image first");
    setBusy(true);
    try {
      await api.post("/card-backs", { ...form, image_file_id: image.file_id });
      toast.success("Card back added to the library");
      qc.invalidateQueries({ queryKey: ["card-backs"] });
      qc.invalidateQueries({ queryKey: ["me", "card-backs"] });
      setImage(null);
      setForm({ name: "", description: "", visibility: "public" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-[#2A3447] bg-[#121620] text-slate-200" data-testid="upload-card-back-dialog">
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Upload a card back</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2">
          <div className="aspect-[63/88]">
            {image ? <img src={assetUrl(image.url)} alt="Card back preview" className="h-full w-full rounded-xl border border-amber-900/40 object-cover" data-testid="card-back-preview" />
              : <ImageDropzone onFiles={pick} label="Drop card back image" testid="card-back-dropzone" compact />}
          </div>
          <div className="space-y-4">
            <div>
              <label className="field-label" htmlFor="back-name">Name</label>
              <input id="back-name" required className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="card-back-name-input" />
            </div>
            <div>
              <label className="field-label" htmlFor="back-desc">Description</label>
              <textarea id="back-desc" rows={3} className="field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="card-back-description-input" />
            </div>
            <div>
              <label className="field-label" htmlFor="back-vis">Visibility</label>
              <select id="back-vis" className="field capitalize" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="card-back-visibility-select">
                {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            {image && <button type="button" className="btn-ghost w-full text-xs" onClick={() => setImage(null)} data-testid="card-back-change-image">Choose a different image</button>}
            <button type="submit" className="btn-gold w-full" disabled={busy || !image} data-testid="card-back-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save card back</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CardBacks() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("library");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (params.get("upload") && user) setOpen(true);
  }, [params, user]);

  const { data, isLoading } = useQuery({
    queryKey: tab === "mine" ? ["me", "card-backs", "page"] : ["card-backs", page],
    queryFn: () => api.get(tab === "mine" ? "/me/card-backs" : "/card-backs", { params: { page, page_size: tab === "mine" ? 200 : 30 } }).then((r) => r.data),
  });

  const remove = async (back) => {
    if (!window.confirm(`Delete card back "${back.name}"? Cards using it will fall back to the default back.`)) return;
    try {
      await api.delete(`/card-backs/${back.card_back_id}`);
      toast.success("Card back deleted");
      qc.invalidateQueries({ queryKey: ["me", "card-backs"] });
      qc.invalidateQueries({ queryKey: ["card-backs"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Sleeves of the multiverse" title="Card Back Library" testid="card-backs-header"
        description="Custom card backs for your cards — and, in a future phase, your decks."
        actions={user && <button className="btn-gold" onClick={() => setOpen(true)} data-testid="upload-card-back-button"><Plus className="h-4 w-4" />Upload card back</button>} />
      {user && (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }} className="mb-8">
          <TabsList className="bg-[#121620]">
            <TabsTrigger value="library" data-testid="card-backs-tab-library">Community library</TabsTrigger>
            <TabsTrigger value="mine" data-testid="card-backs-tab-mine">My card backs</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {isLoading ? null : data?.items?.length ? (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="card-backs-grid">
            {data.items.map((b) => <CardBackTile key={b.card_back_id} back={b} onDelete={user && b.owner_id === user.user_id ? remove : null} />)}
          </div>
          {tab === "library" && <Pager page={page} pageSize={30} total={data.total} onChange={setPage} testid="card-backs-pager" />}
        </>
      ) : (
        <EmptyState icon={Gem} title={tab === "mine" ? "You haven't uploaded any card backs" : "No public card backs yet"} testid="card-backs-empty" />
      )}
      <UploadBackDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && params.get("upload")) setParams({}, { replace: true }); }} />
    </div>
  );
}
